import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CurrencyFormat from 'react-currency-format';
import moment from 'moment';
import '../styles/styles.css';

const useStyles = makeStyles((theme) => ({
  teams: {
    color: '#323030',
  },
  reject: {
    textDecoration: 'line-through',
  },
  bonus: {
    color: '#0b6e99',
    fontWeight: 600,
  },
  // the reference is a link, so it wears the screens' own link: the colour and pointer the drill-down
  // usernames already use, rather than a button or a bracket of its own
  reference: {
    cursor: 'pointer',
  },
}));

const Money = ({ value, className }) => (
  <CurrencyFormat
    value={value}
    decimalScale={2}
    displayType={'text'}
    thousandSeparator={true}
    fixedDecimalScale={true}
    className={className}
  />
);

const betResult = (bet) =>
  bet.result
    ? bet.result.charAt(0).toUpperCase() +
      bet.result.slice(1).toLowerCase().replace('_', ' ')
    : null;

/**
 * One games (Wega/SABA) row, in the sports listing's columns.
 *
 * <p>The columns and their meanings are the sports row's ({@code BetDetail}): the money is the same
 * 2-dp table, a negative figure is red, and Reject/Cancelled is expressed where the sports row expresses
 * it - in the Result cell, with the bet itself struck through. What differs is only what the row has:
 * "Bet Type" is the sport the bet was reported under (the games bucket for a games row, the real sport
 * for a sports-accounted one) and "Bet Details" is the category and the game, since a games row carries
 * no teams, no odds, no handicap and no score.</p>
 *
 * <p>{@code Winning} is the sports row's own basis - winning plus commission, the figure this screen used
 * to call "Net Win" - and {@code Net Amt} is its turnover less commission, so the two money columns mean
 * what they mean on the sports list beside it. Commission is zero on games rows today, so no figure moves
 * for a reader used to the old pair of columns.</p>
 *
 * <p>A bonus is a different thing on the same list, and has to be readable as one: SABA's bonuses are
 * standalone features - a slot jackpot or a special promotion, "just like a ticket" - with no stake behind
 * them, so their row carries no amount and no turnover, and the money they credited is in Winning like a
 * settlement's is. A bonus is therefore deduced from the row itself - {@code amount === 0} - and shown as
 * such: "Bet Type" reads Bonus instead of a sport, the four stake columns read "-" because there is no stake,
 * and Result is "-" because a bonus has no verdict. A bonus that was cancelled reads Cancelled like any other
 * cancelled row, with no winning left on it.</p>
 *
 * <p>The reference is the link: it names the bet, so it is the way in - the vendor's own detail page opens
 * from it (Andrew, 2026-09-26). It wears the screens' own link (the colour and pointer the drill-down
 * usernames use), because an operator already reads that as clickable, and there is nothing new to learn. A
 * cancelled row keeps it: the vendor's page is what says the bet was cancelled, and the row's own
 * strikethrough stays on the columns it always struck.</p>
 */
const WegaBetDetail = ({ bet, index, onReference }) => {
  const classes = useStyles();

  const cancelled = bet.status === 'CANCELLED';
  // a bonus is a zero-stake row: SABA's bonuses are standalone features with no stake behind them, there is no
  // separate column for one, and `winning` carries the amount (Andrew, 2026-09-22). A missing amount is not a
  // bonus - only an explicit zero is.
  const isBonus = bet.amount !== null && bet.amount !== undefined && Number(bet.amount) === 0;
  const netWin = (bet.winning || 0) + (bet.commission || 0);
  const netAmount = (bet.validTurnover || 0) - (bet.commission || 0);
  const result = betResult(bet);

  return (
    <tr>
      <td className="text-dark text-center">{index + 1}</td>
      <td className="text-center">{bet.username}</td>
      <td className="text-center">
        {moment(bet.created).format('DD/MM/YYYY HH:mm:ss')}
      </td>
      <td className="text-center">
        {bet.reference && onReference && bet.pubId ? (
          <a
            className={`${classes.reference} text-primary p-0`}
            onClick={() => onReference(bet.pubId)}
          >
            {bet.reference}
          </a>
        ) : (
          bet.reference
        )}
      </td>
      <td className="text-center">
        {isBonus ? <span className={classes.bonus}>Bonus</span> : bet.sport}
      </td>
      <td className={classes.teams}>
        <span className={cancelled ? classes.reject : ''}>
          {bet.category || bet.categoryCode}
        </span>
        {bet.game ? (
          <>
            <br />
            <span className={cancelled ? classes.reject : ''}>{bet.game}</span>
          </>
        ) : null}
      </td>
      <td className="text-right">
        {isBonus ? <center>-</center> : <Money value={bet.amount || 0} />}
      </td>
      <td className={`text-right ${bet.validTurnover < 0 ? 'text-danger' : ''}`}>
        {isBonus ? <center>-</center> : <Money value={bet.validTurnover || 0} />}
      </td>
      <td className={`text-right ${bet.commission < 0 ? 'text-danger' : ''}`}>
        {isBonus ? <center>-</center> : <Money value={bet.commission || 0} />}
      </td>
      <td className={`text-right ${netAmount < 0 ? 'text-danger' : ''}`}>
        {isBonus ? <center>-</center> : <Money value={netAmount} />}
      </td>
      <td className="text-center">
        {cancelled ? (
          <span className="text-danger">Cancelled</span>
        ) : isBonus ? (
          <center>-</center>
        ) : (
          <span
            className={
              result === 'Win' || result === 'Win half'
                ? 'text-success'
                : result === 'Lose' || result === 'Lose half'
                ? 'text-danger'
                : ''
            }
          >
            {result}
          </span>
        )}
      </td>
      <td className={`text-right ${netWin < 0 ? 'text-danger' : ''}`}>
        {bet.winning ? (
          <Money value={netWin} />
        ) : (
          <center>-</center>
        )}
      </td>
    </tr>
  );
};

export default WegaBetDetail;
